<template>
	<div :class="tooltipClasses">
		<div :class="['map-tooltip-header']">
			{{ $t('home-map.tooltip.headers.' + popupType) }}
		</div>
		<div class="map-tooltip-content">
			<div class="nobr">
				{{ $t('home-map.tooltip.username') }}:
				{{ hoveredFeatureData.username }}
			</div>
			<div class="nobr">
				{{ $t('home-map.tooltip.sessionId') }}:
				{{ hoveredFeatureData.session_id }}
			</div>
			<div class="nobr">
				{{ $t('home-map.tooltip.quality') }}:
				<GpsFixQualityDisplay :quality="hoveredFeatureData.quality" />
			</div>
			<div class="nobr">
				{{ $t('home-map.tooltip.ipAddress') }}:
				{{ hoveredFeatureData.ip_address }}
			</div>
			<div class="nobr" v-if="hoveredFeatureData.current_datetime">
				{{ $t('home-map.tooltip.currentDatetime') }}:
				<DateTimeDisplay :date="hoveredFeatureData.current_datetime" :format="'YYYY-MM-DD HH:mm:ss'" />
			</div>
			<div class="nobr" v-if="hoveredFeatureData.end_datetime">
				{{ $t('home-map.tooltip.endDatetime') }}:
				<DateTimeDisplay :date="hoveredFeatureData.end_datetime" :format="'YYYY-MM-DD HH:mm:ss'" />
			</div>
			<VerticalLayout v-if="qualityStatsDisplay.length > 0" size="small" class="pd-vertical">
				<div class="nobr" v-for="item in qualityStatsDisplay" :key="item.quality">
					<GpsFixQualityDisplay :quality="item.quality" />
					{{ $t('rovers.activeSessions.stateHistoryChart.timesLabel', item.count) }}
				</div>
			</VerticalLayout>
		</div>
	</div>
</template>

<script lang="ts">
import { computed, toRef } from 'vue';
import type { PropType } from 'vue';
import { useLocaleData } from '@/locales/useLocaleData';

import Utils from '@/api/Utils';

import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue';
import GpsFixQualityDisplay from '@/components/common/GpsFixQualityDisplay.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import type {
	FeatureType,
	RoverHistoryCurrentFeatureData,
	RoverHistoryFragmentFeatureData,
} from '@/components/map/types/mapFeatureTypes';

type RoverHistoryTooltipFeatureData = (RoverHistoryCurrentFeatureData | RoverHistoryFragmentFeatureData) & {
	username?: string;
	session_id?: number;
	ip_address?: string;
	current_datetime?: string;
	end_datetime?: string;
	quality_stats?: Record<string, number>;
};

export default {
	components: {
		DateTimeDisplay, GpsFixQualityDisplay, VerticalLayout,
	},
	props: {
		hoveredFeatureData: {
			type: Object as PropType<RoverHistoryTooltipFeatureData>,
			required: true,
		},
		featureType: {
			type: String as PropType<FeatureType>,
			required: true,
		},
	},
	setup(props) {
		const { localeData } = useLocaleData();
		const tooltipClasses = computed(() => [
			'map-tooltip-style',
			'map-tooltip-position-' + localeData.value.direction,
			'no-pointer-events',
		]);
		const hoveredFeatureDataRef = toRef(props, 'hoveredFeatureData');
		const featureTypeRef = toRef(props, 'featureType');
		const popupType = computed(() => {
			if (featureTypeRef.value === 'roverHistoryFragment' && hoveredFeatureDataRef.value.is_break) {
				return 'roverHistoryFragmentBreakEnd';
			}
			return featureTypeRef.value;
		});
		const qualityStatsDisplay = computed(() => {
			if (!['roverHistoryFragment', 'roverHistoryFragmentBreakEnd'].includes(popupType.value)) {
				return [];
			}
			const qualityStats = hoveredFeatureDataRef.value.quality_stats;
			if (qualityStats == null) {
				return [];
			}
			const result = Object.entries(qualityStats).map(([quality, count]) => ({ quality: Number(quality), count }));
			return Utils.sortArray(result, Utils.objectFieldMultiSorter('count', 'number', true));
		});
		return {
			tooltipClasses,
			popupType,
			qualityStatsDisplay,
		}
	}
};
</script>

<style scoped>
@import '@/assets/map-tooltip.css';
</style>